<script lang="ts">
  /**
   * 顧客詳細左カラム「利用ソリューション」 パネル。
   *
   * - 配置: FirmSummary / LawyerDetail の「属性」 セクションの下
   * - 保存値: customer_competitor_services の顧客 x 他社ソリューション利用ファクト
   * - 派生表示: 外部ソリューション本体の保存区分 (`kind`) に基づき、
   *   「競合ソリューションの利用」 / 「紹介先ソリューションの利用」 /
   *   「その他ソリューション」に分けて表示する。
   * - 追加導線は `+ 利用ソリューションを追加` の一本のみ。営業担当は
   *   他社ソリューションを選ぶ。分類はマスターの区分から決まる。
   * - 追加・編集は同一の `CompetitorServiceModal` を開いて行う
   * - 親カード (FirmSummary / LawyerDetail の section) 内の通常セクションとして
   *   表示し、内側に .panel 相当のカード枠を重ねない (二重カード回避)。
   *
   * `target` で事務所単位 (firm) / 弁護士単位 (lawyer) を切り替える。 catalog
   * No.43-45 (事務所詳細タブ) と No.107-109 (弁護士詳細タブ) は別集計、
   * `customer_competitor_services` テーブル上は XOR (firm_id / lawyer_id)。
   */
  import CompetitorServiceModal from '@features/competitor-service-edit/CompetitorServiceModal.svelte';
  import {
    classifySolutionUsages,
    type ClassifiedSolutionUsage,
  } from '@domain/competitor-service/solution-usage';
  import {
    competitorServiceOptionsStore,
    customerCompetitorServicesStore,
  } from '@entities/competitor-service/store.svelte';
  import {
    CUSTOMER_COMPETITOR_SWITCHABILITY_LABELS,
    CUSTOMER_COMPETITOR_USAGE_STATUS_LABELS,
    type CompetitorServiceTarget,
    type CustomerCompetitorService,
  } from '@shared/types/customer-competitor-service';

  interface Props {
    /**
     * 集計対象。 `{ kind: 'firm', firmId }` で事務所詳細、
     * `{ kind: 'lawyer', lawyerId }` で弁護士詳細。 ID が確定していない
     * (= 事務所未所属の弁護士、 など) 場合は `null` を渡すとパネルは
     * empty/disabled になる。
     */
    target: CompetitorServiceTarget | null;
    /** customer_db_edit 権限。 */
    canEdit?: boolean;
    /**
     * パネル見出し。 事務所詳細では「利用ソリューション」、 弁護士詳細では
     * 「利用ソリューション (弁護士単位)」 のような表記にして使い分ける。
     */
    title?: string;
  }

  const {
    target,
    canEdit = false,
    title = '利用ソリューション',
  }: Props = $props();

  let editingService = $state<CustomerCompetitorService | null>(null);
  let showAddModal = $state(false);

  // 他社ソリューションマスターを検索 + 候補選択の母集合としてロードする。 id を
  // 保存対象 (competitor_service_id) に使い、 kind はモーダルでの表示分類判定に使う。
  // マスターが空ならモーダルは候補なし扱いになる。
  const options = $derived(
    competitorServiceOptionsStore.data.map((m) => ({
      id: m.id,
      name: m.name,
      kind: m.kind,
      competitorProductNames: m.competitor_product_names ?? [],
    })),
  );
  const classifiedUsages = $derived(
    classifySolutionUsages(
      customerCompetitorServicesStore.data,
      competitorServiceOptionsStore.data,
    ),
  );
  const competitorUsages = $derived(classifiedUsages.competitorUsages);
  const referralUsages = $derived(classifiedUsages.referralUsages);
  const otherUsages = $derived(classifiedUsages.otherUsages);

  const targetKey = $derived(targetIdentity(target));
  const emptyMessage = $derived(
    target?.kind === 'lawyer'
      ? '弁護士単位の利用ソリューションはまだ登録されていません。'
      : '登録された利用ソリューションはありません。',
  );
  const missingTargetMessage = $derived(
    target == null
      ? '所属事務所が未設定のため利用ソリューションを管理できません。'
      : null,
  );

  function targetIdentity(t: CompetitorServiceTarget | null): string {
    if (t == null) return '';
    return t.kind === 'firm' ? `firm:${t.firmId}` : `lawyer:${t.lawyerId}`;
  }

  function formatMmDd(date: string | null | undefined): string | null {
    if (!date) return null;
    const m = /^\d{4}-(\d{2})-(\d{2})/.exec(date);
    return m ? `${m[1]}/${m[2]}` : date;
  }

  function deadlineSummary(svc: CustomerCompetitorService): string {
    const rej = formatMmDd(svc.renewal_rejection_deadline);
    const end = formatMmDd(svc.contract_end_date);
    if (!rej && !end) return '期限未設定';
    const parts: string[] = [];
    if (rej) parts.push(`更新拒絶 ${rej}`);
    if (end) parts.push(`終了 ${end}`);
    return parts.join('  ');
  }

  function usageStatusLabel(svc: CustomerCompetitorService): string {
    return (
      CUSTOMER_COMPETITOR_USAGE_STATUS_LABELS[svc.usage_status] ??
      svc.usage_status
    );
  }

  function switchabilityLabel(svc: CustomerCompetitorService): string {
    if (!svc.switchability) return '未設定';
    return (
      CUSTOMER_COMPETITOR_SWITCHABILITY_LABELS[svc.switchability] ??
      svc.switchability
    );
  }

  $effect(() => {
    // target が切り替わる (firm <-> lawyer or null <-> ある) たびに再ロード。
    // 文字列化した key を依存にすることで識別する。
    if (targetKey !== '') {
      void customerCompetitorServicesStore.load(target!);
    } else {
      customerCompetitorServicesStore.clear();
    }
    return () => {
      customerCompetitorServicesStore.clear();
    };
  });

  $effect(() => {
    void competitorServiceOptionsStore.load();
    return () => {
      competitorServiceOptionsStore.clear();
    };
  });

  function openAdd(): void {
    // master 取得中は options=[] で開いて自由入力に落ちると分類判定がぶれるため待つ。
    if (
      target == null ||
      !canEdit ||
      competitorServiceOptionsStore.loading ||
      competitorServiceOptionsStore.error != null
    )
      return;
    editingService = null;
    showAddModal = true;
  }

  function openEdit(svc: CustomerCompetitorService): void {
    if (
      target == null ||
      !canEdit ||
      competitorServiceOptionsStore.loading ||
      competitorServiceOptionsStore.error != null
    )
      return;
    editingService = svc;
    showAddModal = true;
  }

  function closeModal(): void {
    showAddModal = false;
    editingService = null;
  }

  function handleSaved(): void {
    closeModal();
    if (target != null) void customerCompetitorServicesStore.load(target);
    void competitorServiceOptionsStore.load();
  }

  function competitorProductLabel(
    row: ClassifiedSolutionUsage<CustomerCompetitorService>,
  ): string {
    return row.competitorProductNames.length > 0
      ? row.competitorProductNames.join('、')
      : '競合設定あり';
  }
</script>

<section
  class="competitor-services-panel"
  data-testid="competitor-services-panel"
  data-target-kind={target?.kind ?? ''}
  data-firm-id={target?.kind === 'firm' ? target.firmId : ''}
  data-lawyer-id={target?.kind === 'lawyer' ? target.lawyerId : ''}
  data-service-count={customerCompetitorServicesStore.data.length}
  data-competitor-usage-count={competitorUsages.length}
  data-referral-usage-count={referralUsages.length}
  data-other-usage-count={otherUsages.length}
>
  <header class="head">
    <h3>{title}</h3>
    {#if canEdit && target != null}
      <button
        type="button"
        class="add-btn"
        data-testid="solution-usage-add"
        aria-label="利用ソリューションを追加"
        disabled={competitorServiceOptionsStore.loading ||
          competitorServiceOptionsStore.error != null}
        onclick={openAdd}
      >
        + 利用ソリューションを追加
      </button>
    {/if}
  </header>

  {#if customerCompetitorServicesStore.error}
    <p class="error-msg" role="alert" data-testid="competitor-services-error">
      {customerCompetitorServicesStore.error}
    </p>
  {/if}
  {#if competitorServiceOptionsStore.error}
    <p
      class="error-msg"
      role="alert"
      data-testid="competitor-services-master-error"
    >
      他社ソリューションマスターを取得できないため、区分を判定できません。{competitorServiceOptionsStore.error}
    </p>
  {/if}

  {#if missingTargetMessage}
    <p class="muted small" data-testid="competitor-services-empty">
      {missingTargetMessage}
    </p>
  {:else if customerCompetitorServicesStore.loading && customerCompetitorServicesStore.data.length === 0}
    <p class="muted small">読み込み中...</p>
  {:else if competitorServiceOptionsStore.loading && customerCompetitorServicesStore.data.length > 0}
    <p
      class="muted small"
      data-testid="competitor-services-classification-loading"
    >
      分類判定に必要なマスターを読み込み中です...
    </p>
  {:else if customerCompetitorServicesStore.data.length === 0}
    <p class="muted small" data-testid="competitor-services-empty">
      {emptyMessage}
    </p>
  {:else if competitorServiceOptionsStore.error}
    <p
      class="muted small"
      data-testid="competitor-services-classification-blocked"
    >
      分類判定に必要なマスターを再取得してから表示します。
    </p>
  {:else}
    <div class="usage-sections" data-testid="solution-usage-sections">
      <section
        class="usage-section"
        data-testid="competitor-solution-usage-section"
      >
        <h4>競合ソリューションの利用</h4>
        {#if competitorUsages.length === 0}
          <p class="muted small section-empty">登録はありません。</p>
        {:else}
          <ul class="service-list" data-testid="competitor-solution-usage-list">
            {#each competitorUsages as row (row.service.id)}
              <li
                class="service-row competitor-usage"
                data-testid="competitor-service-row"
                data-service-id={row.service.id}
                data-competitor-service-id={row.service.competitor_service_id}
              >
                <button
                  type="button"
                  class={`row-button ${canEdit ? '' : 'readonly'}`}
                  disabled={!canEdit}
                  onclick={() => openEdit(row.service)}
                >
                  <span class="row-title-line">
                    <span
                      class="row-name"
                      data-testid="competitor-service-name"
                    >
                      {row.service.competitor_service_name}
                    </span>
                    <span
                      class={`kind-badge ${row.externalSolutionKind}`}
                      data-testid="external-solution-kind"
                      data-external-solution-kind={row.externalSolutionKind}
                    >
                      {row.externalSolutionKindLabel}
                    </span>
                  </span>
                  <span
                    class="row-products"
                    data-testid="competitor-service-products"
                  >
                    対象自社ソリューション: {competitorProductLabel(row)}
                  </span>
                  <span
                    class="row-deadlines muted small"
                    data-testid="competitor-service-deadlines"
                  >
                    {deadlineSummary(row.service)}
                  </span>
                  <span
                    class="row-meta"
                    data-testid="competitor-service-usage-status"
                  >
                    利用状況: {usageStatusLabel(row.service)} / 切替余地: {switchabilityLabel(
                      row.service,
                    )}
                  </span>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </section>

      <section
        class="usage-section"
        data-testid="referral-solution-usage-section"
      >
        <h4>紹介先ソリューションの利用</h4>
        {#if referralUsages.length === 0}
          <p class="muted small section-empty">登録はありません。</p>
        {:else}
          <ul class="service-list" data-testid="referral-solution-usage-list">
            {#each referralUsages as row (row.service.id)}
              <li
                class="service-row referral-usage"
                data-testid="competitor-service-row"
                data-service-id={row.service.id}
                data-competitor-service-id={row.service.competitor_service_id}
              >
                <button
                  type="button"
                  class={`row-button ${canEdit ? '' : 'readonly'}`}
                  disabled={!canEdit}
                  onclick={() => openEdit(row.service)}
                >
                  <span class="row-title-line">
                    <span
                      class="row-name"
                      data-testid="competitor-service-name"
                    >
                      {row.service.competitor_service_name}
                    </span>
                    <span
                      class={`kind-badge ${row.externalSolutionKind}`}
                      data-testid="external-solution-kind"
                      data-external-solution-kind={row.externalSolutionKind}
                    >
                      {row.externalSolutionKindLabel}
                    </span>
                  </span>
                  <span
                    class="row-meta"
                    data-testid="competitor-service-usage-status"
                  >
                    利用状況: {usageStatusLabel(row.service)}
                  </span>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </section>

      <section class="usage-section" data-testid="other-solution-usage-section">
        <h4>その他ソリューション</h4>
        {#if otherUsages.length === 0}
          <p class="muted small section-empty">登録はありません。</p>
        {:else}
          <ul class="service-list" data-testid="other-solution-usage-list">
            {#each otherUsages as row (row.service.id)}
              <li
                class="service-row other-usage"
                data-testid="competitor-service-row"
                data-service-id={row.service.id}
                data-competitor-service-id={row.service.competitor_service_id}
              >
                <button
                  type="button"
                  class={`row-button ${canEdit ? '' : 'readonly'}`}
                  disabled={!canEdit}
                  onclick={() => openEdit(row.service)}
                >
                  <span class="row-title-line">
                    <span
                      class="row-name"
                      data-testid="competitor-service-name"
                    >
                      {row.service.competitor_service_name}
                    </span>
                    <span
                      class={`kind-badge ${row.externalSolutionKind}`}
                      data-testid="external-solution-kind"
                      data-external-solution-kind={row.externalSolutionKind}
                    >
                      {row.externalSolutionKindLabel}
                    </span>
                  </span>
                  <span
                    class="row-meta"
                    data-testid="competitor-service-usage-status"
                  >
                    利用状況: {usageStatusLabel(row.service)}
                  </span>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </section>
    </div>
  {/if}
</section>

{#if showAddModal && target != null}
  <CompetitorServiceModal
    {target}
    {options}
    initialValue={editingService}
    {canEdit}
    onClose={closeModal}
    onSaved={handleSaved}
    onDeleted={handleSaved}
  />
{/if}

<style>
  /* 親カード (FirmSummary / LawyerDetail の section) 内に置くため、ここでは
     カード枠 (border / background / padding) を持たず、見出し・ボタン・
     セクション・行の余白だけで情報を区切る。 */
  .competitor-services-panel {
    display: grid;
    gap: 8px;
  }

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }

  .head h3 {
    margin: 0;
    font-size: 14px;
    color: #0f172a;
  }

  .add-btn {
    appearance: none;
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #1d4ed8;
    border-radius: 6px;
    min-height: 24px;
    line-height: 1;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 5px 9px;
    white-space: nowrap;
  }
  .add-btn:hover {
    background: #eff6ff;
  }
  .add-btn:disabled {
    cursor: not-allowed;
    color: #94a3b8;
    background: #f8fafc;
  }

  .error-msg {
    margin: 0;
    padding: 6px 8px;
    border-radius: 6px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
    font-size: 12px;
    font-weight: 700;
  }

  .usage-sections {
    display: grid;
    gap: 12px;
  }

  .usage-section {
    display: grid;
    gap: 4px;
  }

  .usage-section h4 {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    color: #334155;
    font-weight: 800;
  }

  .section-empty {
    margin: 0;
  }

  .service-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 0;
  }

  .service-row {
    border-top: 1px solid #e5e7eb;
  }
  .service-row:first-child {
    border-top: none;
  }

  .row-button {
    appearance: none;
    border: 0;
    background: transparent;
    width: 100%;
    text-align: left;
    padding: 8px 0;
    cursor: pointer;
    display: grid;
    gap: 2px;
  }
  .row-button.readonly {
    cursor: default;
  }
  .row-button:hover:not(.readonly):not(:disabled) {
    background: #f8fafc;
  }
  .row-button:disabled {
    cursor: default;
  }

  .row-title-line {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .row-name {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    word-break: break-word;
  }

  .kind-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 18px;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    padding: 2px 7px;
    background: #f8fafc;
    color: #374151;
    font-size: 10px;
    line-height: 1.2;
    font-weight: 800;
    white-space: nowrap;
  }
  .kind-badge.competitor {
    border-color: #fecaca;
    background: #fff1f2;
    color: #b91c1c;
  }
  .kind-badge.referral {
    border-color: #bbf7d0;
    background: #f0fdf4;
    color: #166534;
  }
  .kind-badge.other {
    border-color: #d1d5db;
    background: #f8fafc;
    color: #374151;
  }

  .row-products {
    color: #1d4ed8;
    font-size: 11px;
    line-height: 1.4;
    word-break: break-word;
  }

  .row-deadlines {
    font-size: 11px;
    color: #475569;
  }

  .row-meta {
    font-size: 11px;
    line-height: 1.45;
    color: #475569;
    word-break: break-word;
  }

  .muted {
    color: #6b7280;
  }
  .small {
    font-size: 11px;
  }
</style>
